import {
    FloatingFocusManager,
    FloatingList,
    FloatingPortal,
    autoUpdate,
    flip,
    useClick,
    useFloating,
    useInteractions,
    useListNavigation,
    useRole
} from '@floating-ui/react';
import { ICONS } from '@frontend/icons';
import { CommonMessage } from '@frontend/lang';
import React, { ReactNode, createContext, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { LuChevronDown, LuChevronUp, LuX } from 'react-icons/lu';

import { SingleOption } from './select-types';

export interface SelectContextValue {
    getItemProps: ReturnType<typeof useInteractions>['getItemProps'];
    handleSelect: (option: SingleOption<any>) => void;
}
export const SelectContext = createContext<SelectContextValue>({} as SelectContextValue);

const SCROLL_TOLLERANCE = 4;
const ID = 'select-input';
interface SelectProps<T> {
    id?: string;
    ariaLabel?: string;
    placeholder?: string;
    onChange?: (value: string) => void;
    onSelect?: (option: SingleOption<T> | null) => void;
    onScrollToBottom?: () => void;
    value?: string;
    selected?: SingleOption<T> | null;
    isClearable?: boolean;
    children: ReactNode;

    optionsLoading?: boolean;
    onFocus?: React.FocusEventHandler<HTMLInputElement> | undefined;
    onBlur?: React.FocusEventHandler<HTMLInputElement> | undefined;
    disabled?: boolean;
    isOpenOverwrite?: boolean;
    inputRefCallback?: (ref: React.RefObject<HTMLInputElement | null>) => void;
    hidePlaceholder?: boolean;
    containerClassName?: string;
    className?: string;
    disableSearch?: boolean;
}

export function Select<T>(props: SelectProps<T>) {
    const containerRef = useRef<HTMLDivElement>(null);
    const inputRef = useRef<HTMLInputElement>(null);
    const listRef = useRef<HTMLUListElement>(null);
    const [containerWidth, setContainerWidth] = useState(0);
    const [isOpen, setIsOpen] = useState(false);
    const [activeIndex, setActiveIndex] = useState<number | null>(null);
    const firstChildRef = useRef(null);

    const { refs, floatingStyles, context } = useFloating({
        placement: 'bottom-start',
        whileElementsMounted: autoUpdate,
        middleware: [flip()]
    });

    useEffect(() => {
        if (inputRef && props.inputRefCallback) props.inputRefCallback(inputRef);
    }, [inputRef, props.inputRefCallback]);
    useEffect(() => {
        if (isOpen) inputRef.current?.focus();
    }, [isOpen]);

    const updateIsOpen = (value: boolean) => {
        if (props.disabled) return;
        if (props.isOpenOverwrite === undefined) setIsOpen(value);
        else setIsOpen(props.isOpenOverwrite);
    };

    useEffect(() => {
        if (props.isOpenOverwrite !== undefined) setIsOpen(props.isOpenOverwrite);
        else setIsOpen(false);
    }, [props.isOpenOverwrite]);

    useEffect(() => {
        const updateListWidth = () => {
            if (containerRef.current) setContainerWidth(containerRef.current.offsetWidth);
        };
        const handleClickOutside = (event: MouseEvent) => {
            if (
                containerRef.current &&
                event.target &&
                !containerRef.current.contains(event.target as Node) &&
                !refs.floating.current?.contains(event.target as Node)
            ) {
                updateIsOpen(false);
            }
        };

        updateListWidth();
        window.addEventListener('resize', updateListWidth);
        document.addEventListener('mousedown', handleClickOutside);
        return () => {
            window.removeEventListener('resize', updateListWidth);
            document.removeEventListener('mousedown', handleClickOutside);
        };
    }, [refs]);

    useEffect(() => {
        if (props.disabled && isOpen) {
            setIsOpen(false);
        }
    }, [props.disabled, isOpen]);

    useEffect(() => {
        const handleListScroll = () => {
            if (listRef.current) {
                if (listRef.current.scrollTop + listRef.current.clientHeight >= listRef.current.scrollHeight - SCROLL_TOLLERANCE) {
                    props.onScrollToBottom && props.onScrollToBottom();
                }
            }
        };

        const listElement = listRef.current;
        listElement?.addEventListener('scroll', handleListScroll);
        return () => {
            listElement?.removeEventListener('scroll', handleListScroll);
        };
    }, [listRef.current, props.onScrollToBottom]);

    const elementsRef = useRef<Array<HTMLElement | null>>([]);
    const labelsRef = useRef<Array<string | null>>([]);

    const handleSelect = useCallback(
        (option: SingleOption<T>) => {
            props.onSelect && props.onSelect(option);
            updateIsOpen(false);
        },
        [props.isOpenOverwrite, props.onSelect]
    );

    const listNav = useListNavigation(context, {
        listRef: elementsRef,
        allowEscape: false,
        activeIndex,
        onNavigate: setActiveIndex
    });
    const click = useClick(context, {
        keyboardHandlers: false
    });
    const role = useRole(context, { role: 'listbox' });

    const { getReferenceProps, getFloatingProps, getItemProps } = useInteractions([listNav, click, role]);

    const selectContext = useMemo(
        (): SelectContextValue => ({
            getItemProps,
            handleSelect
        }),
        [getItemProps, handleSelect]
    );

    useEffect(() => {
        if (isOpen && props.children && listRef.current && listRef.current.children.length > 0) {
            const firstChild = listRef.current.children[0];
            if (firstChild && firstChild instanceof HTMLElement && typeof firstChild.focus === 'function') {
                firstChild.focus();
            }
        }
    }, [isOpen, props.children]);

    return (
        <div
            className={`w-full ${props.containerClassName ? props.containerClassName : ''}`}
            ref={containerRef}>
            <div
                className={`${props.className ? props.className : 'outline-border-default aria-disabled:bg-brand-disabled bg-background-surface inline-flex h-11 w-full min-w-[120px] flex-nowrap items-center justify-start gap-2 overflow-hidden rounded-md px-4 font-medium outline-1 outline-offset-[-1px] aria-disabled:cursor-not-allowed'}`}
                ref={refs.setReference}
                aria-label={props.ariaLabel}
                aria-disabled={props.disabled || props.isOpenOverwrite === false}
                tabIndex={0}
                {...getReferenceProps()}>
                <button
                    data-testid={props.id}
                    className='text-foreground-default w-full flex-1 cursor-pointer text-start text-base font-medium disabled:cursor-not-allowed'
                    onClick={(e) => {
                        e.preventDefault();
                        if (props.disabled || props.isOpenOverwrite === false) return;
                        updateIsOpen(!isOpen);
                    }}
                    disabled={props.disabled || props.isOpenOverwrite === false}
                    aria-disabled={props.disabled || props.isOpenOverwrite === false ? 'true' : 'false'}>
                    {props.selected ? (
                        props.selected.display || props.selected.label || props.selected.value
                    ) : props.hidePlaceholder ? (
                        ''
                    ) : (
                        <span className='text-brand-muted opacity-90'>{CommonMessage.FORMS.SELECT_PLACEHOLDER}</span>
                    )}
                </button>
                {props.isClearable && (props.value || props.selected) && (
                    <span
                        className={`text-brand-muted hover:text-foreground-default ${props.disabled || props.isOpenOverwrite === false ? '' : 'hover:cursor-pointer'}`}
                        onClick={(e) => {
                            if (props.disabled || props.isOpenOverwrite === false) return;
                            e.preventDefault();
                            e.stopPropagation();
                            props.onChange && props.onChange('');
                            props.onSelect && props.onSelect(null);
                        }}
                        id={props.id || ID + '-clear-button'}>
                        <LuX />
                    </span>
                )}
                <span
                    className='text-brand-muted hover:text-foreground-default hover:cursor-pointer'
                    onClick={() => {
                        if (props.disabled || props.isOpenOverwrite === false) return;
                        updateIsOpen(!isOpen);
                    }}
                    id={props.id || ID + '-dropdown-button'}>
                    {isOpen ? <LuChevronUp /> : <LuChevronDown />}
                </span>
            </div>

            <SelectContext.Provider value={selectContext}>
                {isOpen && (
                    <FloatingPortal>
                        <FloatingFocusManager
                            context={context}
                            modal={false}>
                            <div
                                className='z-1051'
                                ref={refs.setFloating}
                                style={floatingStyles}
                                {...getFloatingProps()}>
                                <FloatingList
                                    elementsRef={elementsRef}
                                    labelsRef={labelsRef}>
                                    <ul
                                        ref={listRef}
                                        role='select'
                                        className='list-group ignore-modal-close border-border-default mt-1 border shadow-md'
                                        style={{ minWidth: `${containerWidth}px`, maxHeight: '30vh', overflowY: 'scroll' }}>
                                        {!props.disableSearch && (
                                            <li className='list-group-item option ignore-modal-close bg-background-surface sticky top-0 z-10 flex flex-row items-center border-0 border-b'>
                                                <div className='text-brand-muted'>
                                                    <ICONS.BUTTON.SEARCH className='h-5 w-5' />
                                                </div>
                                                <input
                                                    ref={inputRef}
                                                    id={props.id || ID + '-search-input'}
                                                    aria-describedby={props.id || ID + '-dropdown-button'}
                                                    className='form-control ignore-modal-close border-none text-sm outline-none'
                                                    type='text'
                                                    value={props.value ?? ''}
                                                    onChange={(e) => props.onChange && props.onChange(e.target.value)}
                                                    placeholder={props.placeholder || 'Search...'}
                                                    onFocus={(e) => {
                                                        if (!props.disabled) updateIsOpen(true);
                                                        props.onFocus && props.onFocus(e);
                                                    }}
                                                    onBlur={props.onBlur}
                                                    disabled={props.disabled}
                                                    autoFocus
                                                />
                                            </li>
                                        )}
                                        {props.optionsLoading ? (
                                            <li className='list-group-item option border-none'>{CommonMessage.FORMS.LOADING_PLACEHOLDER}</li>
                                        ) : (
                                            props.children
                                        )}
                                    </ul>
                                </FloatingList>
                            </div>
                        </FloatingFocusManager>
                    </FloatingPortal>
                )}
            </SelectContext.Provider>
        </div>
    );
}
